/**
 * Shared item rendering for Menu (Radix dropdown menu) and ContextMenu (Radix context menu): both libraries
 * expose the same parts, so the item list is written once against this small kit.
 *
 * Item kinds (contract MenuItem):
 *  - command:   label from the item, else the bridge's description of the command (UiBridge.describe),
 *               else the id; shortcut from the keymap; disabled with the registry's reason (shown under the label);
 *               toggles (describe().checked) render as checkbox items; selecting runs the command via the bridge.
 *  - action:    label, icon, disabled reason, onSelect.
 *
 * Disabled items stay reachable (like the kit's buttons): Radix's `disabled` would drop them from arrow-key
 * navigation and typeahead, so keyboard and screen-reader users could never read why. They are aria-disabled
 * instead, describe themselves with the reason (aria-describedby; the reason is kept out of the item's name),
 * and selecting them does nothing and leaves the menu open.
 *  - submenu:   nested menu (ArrowRight / Enter opens it, ArrowLeft / Escape closes it).
 *  - separator.
 */

import { useId, type ComponentType, type ReactNode } from 'react';
import { firstChord, useTr, useUiBridge } from '../bridge';
import { Icon } from '../Icon';
import { Kbd } from '../Kbd';
import type { MenuItem } from '../types';
import s from './Menu.module.css';

interface Common {
  readonly className?: string | undefined;
  readonly children?: ReactNode;
}

/** Props of a selectable item (disabled items are aria-disabled, never Radix-disabled; see module comment). */
interface ItemProps extends Common {
  readonly 'aria-disabled'?: true | undefined;
  readonly 'aria-describedby'?: string | undefined;
  readonly 'data-disabled'?: '' | undefined;
  onSelect?: (e: Event) => void;
}

export interface MenuKit {
  readonly Item: ComponentType<ItemProps>;
  readonly CheckboxItem: ComponentType<ItemProps & { readonly checked?: boolean }>;
  readonly ItemIndicator: ComponentType<Common>;
  readonly Separator: ComponentType<{ readonly className?: string | undefined }>;
  readonly Sub: ComponentType<{ readonly children?: ReactNode }>;
  readonly SubTrigger: ComponentType<Common>;
  readonly SubContent: ComponentType<Common & { readonly sideOffset?: number; readonly alignOffset?: number }>;
  readonly Portal: ComponentType<{ readonly children?: ReactNode }>;
}

interface RowProps {
  readonly icon?: string | undefined;
  readonly label: ReactNode;
  readonly chord?: string | undefined;
  readonly reason?: string | null;
  /** Id of the reason line (the item's aria-describedby). */
  readonly reasonId?: string | undefined;
  readonly submenu?: boolean;
  readonly checked?: boolean | undefined;
  readonly kit: MenuKit;
}

function Row(props: RowProps) {
  const { kit } = props;
  return (
    <>
      <span className={s.lead}>
        {props.checked !== undefined ? (
          <kit.ItemIndicator className={s.indicator}>
            <Icon name="Check" size={14} />
          </kit.ItemIndicator>
        ) : (
          props.icon !== undefined && <Icon name={props.icon} size={14} />
        )}
      </span>
      <span className={s.text}>
        <span className={s.label}>{props.label}</span>
        {props.reason !== undefined && props.reason !== null && (
          // Read as the item's description, not as part of its name.
          <span id={props.reasonId} className={s.reason} aria-hidden>
            {props.reason}
          </span>
        )}
      </span>
      {props.chord !== undefined && <Kbd chord={props.chord} className={s.kbd} />}
      {props.submenu === true && <Icon name="ChevronRight" size={14} />}
    </>
  );
}

/**
 * ARIA and select handling for an item that may be disabled: focusable and described by its reason; selecting a
 * disabled item does nothing and keeps the menu open.
 */
function itemState(reasonId: string, disabled: boolean, onSelect: () => void): ItemProps {
  return {
    'aria-disabled': disabled ? true : undefined,
    'aria-describedby': disabled ? reasonId : undefined,
    'data-disabled': disabled ? '' : undefined,
    onSelect: (e: Event) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      onSelect();
    },
  };
}

function CommandItem(props: { readonly kit: MenuKit; readonly item: Extract<MenuItem, { kind: 'command' }> }) {
  const { kit, item } = props;
  const bridge = useUiBridge();
  const tr = useTr();
  const reasonId = useId();
  const description = bridge.describe?.(item.command) ?? null;
  const label = item.label ?? (description === null ? item.command : tr(description.title));
  const enabled = bridge.enabled(item.command);
  const disabled = enabled !== true;
  const reason = enabled === true ? null : tr(enabled);
  const chord = firstChord(bridge, item.command);
  const run = (): void => {
    bridge.run(item.command, item.args);
  };
  const state = itemState(reasonId, disabled, run);
  if (description?.checked !== undefined) {
    return (
      <kit.CheckboxItem className={s.item} checked={description.checked} {...state}>
        <Row kit={kit} label={label} chord={chord} reason={reason} reasonId={reasonId} checked={description.checked} />
      </kit.CheckboxItem>
    );
  }
  return (
    <kit.Item className={s.item} {...state}>
      <Row kit={kit} icon={description?.icon} label={label} chord={chord} reason={reason} reasonId={reasonId} />
    </kit.Item>
  );
}

function ActionItem(props: { readonly kit: MenuKit; readonly item: Extract<MenuItem, { kind: 'action' }> }) {
  const { kit, item } = props;
  const tr = useTr();
  const reasonId = useId();
  const reason = item.disabledReason === undefined || item.disabledReason === null ? null : tr(item.disabledReason);
  const state = itemState(reasonId, reason !== null, () => {
    item.onSelect();
  });
  return (
    <kit.Item className={s.item} {...state}>
      <Row kit={kit} icon={item.icon} label={item.label} reason={reason} reasonId={reasonId} />
    </kit.Item>
  );
}

export function MenuItems(props: { readonly kit: MenuKit; readonly items: readonly MenuItem[] }) {
  const { kit } = props;
  return (
    <>
      {props.items.map((item, i) => {
        const key = `${item.kind}:${String(i)}`;
        switch (item.kind) {
          case 'separator':
            return <kit.Separator key={key} className={s.separator} />;
          case 'command':
            return <CommandItem key={`${key}:${item.command}`} kit={kit} item={item} />;
          case 'action':
            return <ActionItem key={key} kit={kit} item={item} />;
          case 'submenu':
            return (
              <kit.Sub key={key}>
                <kit.SubTrigger className={s.item}>
                  <Row kit={kit} label={item.label} submenu />
                </kit.SubTrigger>
                <kit.Portal>
                  <kit.SubContent className={s.content} sideOffset={2} alignOffset={-4}>
                    <MenuItems kit={kit} items={item.items} />
                  </kit.SubContent>
                </kit.Portal>
              </kit.Sub>
            );
        }
      })}
    </>
  );
}

export const menuStyles = s;
